/**
 * Domains at risk — dashboard widget shown at the top of /clients (admin
 * home). Highlights domains expiring soon, prioritising ones without
 * auto-renew and those already assigned to a paying client.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, ChevronRight, Repeat, ShieldAlert, Timer } from "lucide-react";

const SEV_STYLE = {
  critical: { cls: "bg-red-50 text-red-700 border-red-200", label: "critical" },
  warning:  { cls: "bg-amber-50 text-amber-700 border-amber-200", label: "warning" },
  monitor:  { cls: "bg-zinc-100 text-zinc-600 border-zinc-200", label: "monitor" },
};

export function DomainsAtRiskWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/domains/at-risk")
      .then((r) => setData(r.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const shown = data?.domains || [];

  if (loading || !data || data.total === 0) return null;

  return (
    <Card className="rounded-sm border-amber-200 bg-amber-50/40"
      data-testid="domains-at-risk-widget">
      <CardContent className="p-4 space-y-3">
        <button type="button" onClick={() => setOpen((s) => !s)}
          className="w-full flex items-center justify-between text-left"
          data-testid="domains-at-risk-toggle">
          <div className="flex items-center gap-2">
            <ShieldAlert className={`h-4 w-4 ${data.critical > 0 ? "text-red-600" : "text-amber-600"}`} />
            <div>
              <div className="label-overline flex items-center gap-1.5">
                Domains at risk
                {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </div>
              <div className="text-[11px] text-zinc-600 font-mono">
                {data.critical > 0 && (
                  <span className="text-red-600 font-medium">{data.critical} critical</span>
                )}
                {data.critical > 0 && data.warning > 0 && " · "}
                {data.warning > 0 && (
                  <span className="text-amber-700">{data.warning} warning</span>
                )}
                {(data.critical > 0 || data.warning > 0) && data.monitor > 0 && " · "}
                {data.monitor > 0 && <span className="text-zinc-500">{data.monitor} monitor</span>}
                <span className="text-zinc-500"> · {data.total} expiring in ≤90 days</span>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" asChild
            className="rounded-sm h-7 text-[11px]"
            data-testid="domains-at-risk-view-all">
            <span role="link" tabIndex={0}
              onClick={(e) => { e.stopPropagation(); navigate("/domains"); }}>
              Manage domains
            </span>
          </Button>
        </button>

        {open && (
          <div className="border border-amber-200/70 rounded-sm bg-white overflow-hidden"
            data-testid="domains-at-risk-list">
            <div className="grid grid-cols-12 gap-2 px-3 py-1.5 bg-zinc-50 border-b border-zinc-200
              text-[9px] font-mono uppercase tracking-wider text-zinc-500 sticky top-0">
              <div className="col-span-4">Domain</div>
              <div className="col-span-2">Expiry</div>
              <div className="col-span-1">Auto</div>
              <div className="col-span-2">Cost</div>
              <div className="col-span-3">Client</div>
            </div>
            <div className="max-h-[420px] overflow-y-auto"
              data-testid="domains-at-risk-scroll">
              {shown.map((d) => (
                <RiskRow key={d.domain_id} d={d} navigate={navigate} />
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RiskRow({ d, navigate }) {
  const sev = SEV_STYLE[d.severity] || SEV_STYLE.monitor;
  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-1.5 border-b border-zinc-100 items-center
      hover:bg-zinc-50/60 transition-colors cursor-pointer text-[11px]"
      onClick={() => navigate("/domains")}
      data-testid={`domain-at-risk-row-${d.domain_id}`}>
      <div className="col-span-4 flex items-center gap-1.5 min-w-0">
        <Badge variant="outline" className={`rounded-sm text-[8px] shrink-0 ${sev.cls}`}>
          {sev.label}
        </Badge>
        <span className="font-mono truncate">{d.domain}</span>
      </div>
      <div className="col-span-2 flex items-center gap-1 min-w-0">
        <Timer className="h-3 w-3 text-zinc-400 shrink-0" />
        <span className="font-mono text-zinc-600 truncate">
          {d.expiry_date} <span className="text-zinc-400">({d.days_left}d)</span>
        </span>
      </div>
      <div className="col-span-1">
        {d.autorenew ? (
          <span className="inline-flex items-center gap-1 text-emerald-700"
            title="Auto-renew ON at TPP">
            <Repeat className="h-3 w-3" /> ON
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-red-600"
            title="Auto-renew OFF — needs manual renewal">
            <AlertTriangle className="h-3 w-3" /> OFF
          </span>
        )}
      </div>
      <div className="col-span-2 font-mono text-zinc-600">
        {d.renewal_cost_api != null
          ? `$${Number(d.renewal_cost_api).toFixed(2)}`
          : <span className="text-zinc-300">—</span>}
      </div>
      <div className="col-span-3 truncate">
        {d.client_name
          ? <span className="text-[#002FA7]">{d.client_name}</span>
          : <span className="text-zinc-400 italic">unassigned</span>}
      </div>
    </div>
  );
}
